<script setup lang="ts">
import { ref, watch, computed, onMounted, onUnmounted } from 'vue';
import type { Idea } from '../types';
import IconButton from './IconButton.vue';
import X from './icons/X.vue';
import Plus from './icons/Plus.vue';

interface Props {
  modelValue: boolean;
  isMobile?: boolean;
  allTags?: string[];
  editMode?: boolean;
  idea?: Idea | null;
  workshopId?: string;
  prefillPerson?: string;
}

const props = withDefaults(defineProps<Props>(), {
  isMobile: false,
  allTags: () => [],
  editMode: false,
  idea: null,
  workshopId: '',
  prefillPerson: '',
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  close: [];
  add: [idea: Omit<Idea, 'id' | 'liked' | 'important' | 'supported'>];
  edit: [idea: Idea];
}>();

const isOpen = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
});

const title = ref('');
const person = ref('');
const explanation = ref('');
const selectedTags = ref<string[]>([]);
const newTagInput = ref('');
const selectedColor = ref<Idea['color']>('yellow');

const availableTags = computed(() => {
  return props.allTags.filter(tag => !selectedTags.value.includes(tag));
});

const colors: { value: Idea['color']; label: string; bg: string; shadow: string }[] = [
  {
    value: 'yellow',
    label: 'Yellow',
    bg: 'bg-(--color-postit-yellow)',
    shadow: 'shadow-yellow-200',
  },
  { value: 'pink', label: 'Pink', bg: 'bg-(--color-postit-pink)', shadow: 'shadow-pink-200' },
  { value: 'blue', label: 'Blue', bg: 'bg-(--color-postit-blue)', shadow: 'shadow-blue-200' },
  {
    value: 'green',
    label: 'Green',
    bg: 'bg-(--color-postit-green)',
    shadow: 'shadow-green-200',
  },
  {
    value: 'orange',
    label: 'Orange',
    bg: 'bg-[var(--color-postit-orange)]',
    shadow: 'shadow-orange-200',
  },
];

const rotation = ref((Math.random() - 0.5) * 3);

watch(isOpen, open => {
  if (open) {
    if (props.editMode && props.idea) {
      // Pre-fill form for editing
      title.value = props.idea.title;
      person.value = props.idea.person || '';
      explanation.value = props.idea.explanation || '';
      selectedTags.value = [...props.idea.tags];
      selectedColor.value = props.idea.color;
      rotation.value = props.idea.rotation;
    } else {
      // Reset form for new idea
      title.value = '';
      // Prefill person if setting is enabled
      person.value = props.prefillPerson || '';
      explanation.value = '';
      selectedTags.value = [];
      newTagInput.value = '';
      selectedColor.value = 'yellow';
      rotation.value = (Math.random() - 0.5) * 3;
    }
  }
});

function handleSubmit() {
  if (!title.value.trim()) return;

  if (props.editMode && props.idea) {
    // Update existing idea
    emit('edit', {
      ...props.idea,
      title: title.value.trim(),
      person: person.value.trim() || undefined,
      explanation: explanation.value.trim(),
      tags: selectedTags.value,
      color: selectedColor.value,
      rotation: rotation.value,
    });
  } else {
    // Add new idea - only send fields that Convex expects
    emit('add', {
      title: title.value.trim(),
      person: person.value.trim() || undefined,
      explanation: explanation.value.trim() || undefined,
      tags: selectedTags.value,
      color: selectedColor.value,
      rotation: rotation.value,
    });
  }

  isOpen.value = false;
  emit('close');
}

function toggleTag(tag: string) {
  const index = selectedTags.value.indexOf(tag);
  if (index > -1) {
    selectedTags.value.splice(index, 1);
  } else {
    selectedTags.value.push(tag);
  }
}

function addNewTag() {
  const tag = newTagInput.value.trim().toLowerCase();
  if (tag && !selectedTags.value.includes(tag)) {
    selectedTags.value.push(tag);
    newTagInput.value = '';
  }
}

function removeTag(tag: string) {
  const index = selectedTags.value.indexOf(tag);
  if (index > -1) {
    selectedTags.value.splice(index, 1);
  }
}

function handleClose() {
  isOpen.value = false;
  emit('close');
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && isOpen.value) {
    handleClose();
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
});

const currentColorClass = computed(() => {
  return colors.find(c => c.value === selectedColor.value)?.bg || 'bg-(--color-postit-yellow)';
});
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen"
        :class="[
          'fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center',
          isMobile ? 'p-2' : 'p-4',
        ]"
        @click="handleClose"
      >
        <!-- Large Post-it Note Modal -->
        <div
          :class="[
            currentColorClass,
            'postit relative rounded-sm shadow-2xl w-full max-w-lg',
            isMobile ? 'max-h-[85vh] overflow-y-auto' : '',
          ]"
          :style="{ transform: isMobile ? 'none' : `rotate(${rotation}deg)` }"
          @click.stop
        >
          <!-- Paper texture overlay -->
          <div class="paper-texture rounded-sm"></div>

          <!-- Tape strip at top -->
          <div
            class="tape-strip"
            :style="{
              '--tape-offset-x': '0%',
              '--tape-offset-y': '2px',
              '--tape-rotation': '-1deg',
              '--tape-width': '5%',
              '--tape-height': '2px',
            }"
          ></div>

          <!-- Header -->
          <div
            :class="[
              'relative z-10 flex items-center justify-between border-b border-black/10',
              isMobile ? 'p-3' : 'p-5',
            ]"
          >
            <h2 class="text-xl font-bold text-gray-800">
              {{ editMode ? $t('idea.edit') : $t('idea.addNew') }}
            </h2>
            <IconButton
              :icon="X"
              size="sm"
              @click="handleClose"
              class="text-gray-700 hover:text-gray-900"
            />
          </div>

          <!-- Form -->
          <form
            @submit.prevent="handleSubmit"
            :class="['relative z-10 space-y-4', isMobile ? 'p-3' : 'p-5']"
          >
            <!-- Title -->
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">
                {{ $t('idea.title') }} <span class="text-red-600">*</span>
              </label>
              <input
                v-model="title"
                type="text"
                required
                :placeholder="$t('idea.titlePlaceholder')"
                class="handwritten-input w-full text-gray-800 bg-transparent/50 rounded px-2"
              />
            </div>

            <!-- Person -->
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">{{
                $t('idea.person')
              }}</label>
              <input
                v-model="person"
                type="text"
                :placeholder="$t('idea.personPlaceholder')"
                class="handwritten-input w-full text-gray-800 bg-transparent/50 rounded px-2"
              />
            </div>

            <!-- Explanation -->
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">{{
                $t('idea.explanation')
              }}</label>
              <textarea
                v-model="explanation"
                :rows="isMobile ? 2 : 3"
                :placeholder="$t('idea.explanationPlaceholder')"
                class="handwritten-input w-full text-gray-800 bg-transparent/50 rounded px-2 resize-none"
              ></textarea>
            </div>

            <!-- Tags -->
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">{{
                $t('idea.tags')
              }}</label>

              <!-- Selected tags -->
              <div v-if="selectedTags.length > 0" class="flex flex-wrap gap-1.5 mb-2">
                <span
                  v-for="tag in selectedTags"
                  :key="tag"
                  class="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-800/80 text-white rounded-full capitalize"
                >
                  {{ tag }}
                  <button type="button" @click="removeTag(tag)" class="hover:text-white/80">
                    <X class="w-3 h-3" />
                  </button>
                </span>
              </div>

              <!-- Available tags -->
              <div v-if="availableTags.length > 0" class="mb-2">
                <p class="text-xs text-gray-600 mb-1">{{ $t('idea.clickToAdd') }}:</p>
                <div class="flex flex-wrap gap-1">
                  <button
                    v-for="tag in availableTags"
                    :key="tag"
                    type="button"
                    @click="toggleTag(tag)"
                    class="px-2 py-0.5 text-xs bg-black/10 text-gray-700 rounded-full hover:bg-black/20 transition-colors capitalize"
                  >
                    {{ tag }}
                  </button>
                </div>
              </div>

              <!-- Add new tag -->
              <div class="flex gap-2">
                <input
                  v-model="newTagInput"
                  type="text"
                  :placeholder="$t('idea.addNewTag')"
                  class="handwritten-input flex-1 text-gray-800 bg-transparent/50 rounded px-2 text-sm"
                  @keydown.enter.prevent="addNewTag"
                />
                <button
                  type="button"
                  @click="addNewTag"
                  :disabled="!newTagInput.trim()"
                  class="px-2 py-1.5 bg-black/10 text-gray-700 rounded-md hover:bg-black/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus class="w-4 h-4" />
                </button>
              </div>
            </div>

            <!-- Color picker - Post-it swatches with tape -->
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">{{
                $t('idea.color')
              }}</label>
              <div :class="['flex', isMobile ? 'gap-2' : 'gap-3']">
                <button
                  v-for="color in colors"
                  :key="color.value"
                  type="button"
                  @click="selectedColor = color.value"
                  :class="[
                    color.bg,
                    'rounded-sm shadow-md transition-all duration-200 relative',
                    isMobile ? 'w-10 h-10' : 'w-12 h-12',
                    selectedColor === color.value
                      ? 'ring-2 ring-offset-2 ring-gray-800 scale-110 shadow-lg'
                      : 'hover:scale-105',
                  ]"
                  :title="color.label"
                >
                  <!-- Mini tape for selected color -->
                  <div
                    v-if="selectedColor === color.value"
                    class="absolute -top-1 left-1/2 -translate-x-1/2 w-6 h-2 bg-white/50 rounded-sm"
                  ></div>
                </button>
              </div>
            </div>

            <!-- Actions -->
            <div :class="['flex gap-3 border-t border-black/10', isMobile ? 'pt-2' : 'pt-3']">
              <button
                type="button"
                @click="handleClose"
                :class="[
                  'flex-1 bg-black/10 text-gray-700 rounded-md hover:bg-black/20 transition-colors font-medium',
                  isMobile ? 'px-3 py-2 text-sm' : 'px-4 py-2.5',
                ]"
              >
                {{ $t('common.cancel') }}
              </button>
              <button
                type="submit"
                :disabled="!title.trim()"
                :class="[
                  'push-button flex-1 rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2',
                  isMobile ? 'px-3 py-2 text-sm' : 'px-4 py-2.5',
                ]"
              >
                <Plus v-if="!editMode" class="w-4 h-4" />
                {{ editMode ? $t('common.save') : $t('idea.addNew') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
